// ═══════════════════════════════════════════════════════════════
// RollCall Kiosk — Self-service check-in station
// Two modes: name search list, or PIN/badge entry.
// Both sources from People Directory (employee + liaison).
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from 'react';
import { IconCheck, IconBackspace, IconX, IconUsers, IconKeyboard } from '@tabler/icons-react';
import { useRollCallStore } from '../../store/rollcall-store';
import { GabButton, GabCard, GabCardBody, GabAlert, GabBadge } from '../../components/gab';
import { GabNativeSelect, GabInput } from '../../components/gab/GabInput';
import { queryRecords } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

type DirectoryPerson = {
  record_id: number;
  fullName: string;
  title: string;
  department: string;
  type: string;
  kioskPin: string;
  badgeId: string;
};

type KioskMode = 'name' | 'pin';

export default function Kiosk() {
  const { events, checkins, fetchEvents, fetchCheckins, checkIn, checkOut } = useRollCallStore();
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [search, setSearch] = useState('');
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState<KioskMode>('name');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [result, setResult] = useState<{ ok: boolean; name: string; action: 'in' | 'out' } | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchCheckins();
    if (!TABLES.people) return;
    const FP = FIELDS.people;
    Promise.all([
      queryRecords<Record<string, unknown>>(TABLES.people, {
        filters: [{ field: FP.type, operator: 'eq', value: 'employee' }],
        sort: { field: FP.lastName, direction: 'asc' }, limit: 500,
      }),
      queryRecords<Record<string, unknown>>(TABLES.people, {
        filters: [{ field: FP.type, operator: 'eq', value: 'liaison' }],
        sort: { field: FP.lastName, direction: 'asc' }, limit: 500,
      }),
    ]).then(([emp, lia]) => {
      const map = (p: Record<string, unknown>): DirectoryPerson => {
        const fn = String(p[FP.firstName] ?? '');
        const ln = String(p[FP.lastName] ?? '');
        return {
          record_id: p.record_id as number,
          fullName: [fn, ln].filter(Boolean).join(' '),
          title: String(p[FP.title] ?? ''),
          department: String(p[FP.department] ?? ''),
          type: String(p[FP.type] ?? ''),
          kioskPin: String(p[FP.kioskPin] ?? ''),
          badgeId: String(p[FP.badgeId] ?? ''),
        };
      };
      const all = [...emp.records.map(map), ...lia.records.map(map)]
        .filter(p => p.fullName.trim())
        .sort((a, b) => a.fullName.localeCompare(b.fullName));
      const seen = new Set<number>();
      setPeople(all.filter(p => { if (seen.has(p.record_id)) return false; seen.add(p.record_id); return true; }));
    }).catch(() => {});
  }, []);

  const activeEvents = useMemo(() => events.filter(e => e.eventStatus === 'open'), [events]);
  const eventOpts = useMemo(() => activeEvents.map(e => ({ value: String(e.id), label: e.eventName })), [activeEvents]);

  useEffect(() => {
    if (activeEvents.length > 0 && !selectedEventId) setSelectedEventId(String(activeEvents[0].id));
  }, [activeEvents]);

  const filtered = useMemo(() => {
    if (!search.trim()) return people;
    const q = search.toLowerCase();
    return people.filter(p => p.fullName.toLowerCase().includes(q) || p.department.toLowerCase().includes(q));
  }, [people, search]);

  const isCheckedIn = (p: DirectoryPerson) =>
    checkins.some(c =>
      String(c.eventId) === selectedEventId &&
      String(c.personId) === String(p.record_id) &&
      !c.checkOutTime
    );

  const doToggle = async (p: DirectoryPerson) => {
    if (!selectedEventId || processing) return;
    setProcessing(true);
    const active = checkins.find(c =>
      String(c.eventId) === selectedEventId &&
      String(c.personId) === String(p.record_id) &&
      !c.checkOutTime
    );
    try {
      if (active) {
        await checkOut(active.id);
        setResult({ ok: true, name: p.fullName, action: 'out' });
      } else {
        await checkIn({
          eventId: Number(selectedEventId),
          personId: String(p.record_id),
          personName: p.fullName,
          agency: p.department || '',
          method: 'badge',
        });
        setResult({ ok: true, name: p.fullName, action: 'in' });
      }
      await fetchCheckins();
    } catch {
      setResult({ ok: false, name: p.fullName, action: active ? 'out' : 'in' });
    } finally {
      setProcessing(false);
      setTimeout(() => setResult(null), 3000);
    }
  };

  // PIN mode lookup
  const handlePinSubmit = useCallback(() => {
    if (!pin || !selectedEventId) return;
    const match = people.find(p =>
      (p.kioskPin && p.kioskPin === pin) ||
      (p.badgeId  && p.badgeId  === pin)
    );
    if (!match) {
      setResult({ ok: false, name: `PIN ${pin}`, action: 'in' });
      setPin('');
      setTimeout(() => setResult(null), 3000);
      return;
    }
    doToggle(match);
    setPin('');
  }, [pin, selectedEventId, people, checkins]);

  // Keyboard support in PIN mode
  useEffect(() => {
    if (mode !== 'pin') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') setPin(p => p.length < 8 ? p + e.key : p);
      else if (e.key === 'Backspace') setPin(p => p.slice(0, -1));
      else if (e.key === 'Enter') handlePinSubmit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, handlePinSubmit]);

  const digits = ['1','2','3','4','5','6','7','8','9','','0',''];

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 'var(--spacing-4)' }}>
      {eventOpts.length > 0 && (
        <div className="mb-4">
          <GabNativeSelect label="Active Event" options={eventOpts} value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)} />
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4">
        <GabButton
          variant={mode === 'name' ? 'primary' : 'ghost'} size="sm"
          leftIcon={<IconUsers size={14} />}
          onClick={() => { setMode('name'); setPin(''); }}
          style={{ flex: 1 }}
        >
          Find by Name
        </GabButton>
        <GabButton
          variant={mode === 'pin' ? 'primary' : 'ghost'} size="sm"
          leftIcon={<IconKeyboard size={14} />}
          onClick={() => { setMode('pin'); setSearch(''); }}
          style={{ flex: 1 }}
        >
          PIN / Badge
        </GabButton>
      </div>

      <GabCard>
        <GabCardBody>
          <h2 className="text-center fw-bold text-xl mb-1">RollCall Check-In</h2>

          {result && (
            <GabAlert variant={result.ok ? 'success' : 'error'} className="mb-4">
              <div className="text-center">
                {result.ok
                  ? <><IconCheck size={28} style={{ display: 'inline' }} /><div className="fw-bold mt-1">{result.action === 'in' ? 'Checked In' : 'Checked Out'}</div></>
                  : <div>PIN / Badge not found — see staff.</div>}
                {result.ok && <div className="text-sm">{result.name}</div>}
              </div>
            </GabAlert>
          )}

          {/* ── Name search mode ── */}
          {mode === 'name' && (
            <>
              <p className="text-center text-sm text-muted mb-3">Find your name and tap to check in or out</p>
              <div className="mb-3">
                <GabInput
                  placeholder="Search by name or department…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filtered.length === 0 ? (
                  <p className="text-center text-muted text-sm py-6">
                    {people.length === 0 ? 'No employees or liaisons in directory.' : 'No matches found.'}
                  </p>
                ) : filtered.map(p => {
                  const in_ = isCheckedIn(p);
                  return (
                    <button
                      key={p.record_id}
                      onClick={() => doToggle(p)}
                      disabled={processing}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 16px',
                        border: `1px solid ${in_ ? 'var(--color-success)' : 'var(--color-neutral-200)'}`,
                        borderRadius: 4, background: in_ ? 'rgba(3,119,48,0.06)' : '#fff',
                        cursor: 'pointer', textAlign: 'left', width: '100%',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{p.fullName}</div>
                        {(p.title || p.department) && (
                          <div style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>
                            {[p.title, p.department].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                      <GabBadge variant={in_ ? 'success' : 'neutral'}>{in_ ? 'IN' : 'Check In'}</GabBadge>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── PIN / Badge mode ── */}
          {mode === 'pin' && (
            <>
              <p className="text-center text-sm text-muted mb-3">Enter your PIN or badge number</p>
              <div className="text-center mb-4 py-3" style={{
                background: 'var(--color-neutral-50)', borderRadius: 'var(--radius-md)',
                fontSize: '2rem', letterSpacing: '0.5em', fontFamily: 'monospace',
              }}>
                {pin.replace(/./g, '●') || '—'}
              </div>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                {digits.map((d, i) => d ? (
                  <button key={i} className="btn btn-outline"
                    style={{ height: 56, fontSize: '1.5rem' }}
                    onClick={() => setPin(p => p.length < 8 ? p + d : p)}>{d}</button>
                ) : i === 9 ? (
                  <button key={i} className="btn btn-ghost" style={{ height: 56 }}
                    onClick={() => setPin('')}><IconX size={20} /></button>
                ) : (
                  <button key={i} className="btn btn-ghost" style={{ height: 56 }}
                    onClick={() => setPin(p => p.slice(0, -1))}><IconBackspace size={20} /></button>
                ))}
              </div>
              <GabButton
                variant="primary" style={{ width: '100%', marginTop: 'var(--spacing-4)', height: 52 }}
                leftIcon={<IconCheck size={20} />}
                onClick={handlePinSubmit}
                disabled={pin.length < 1 || processing}
              >
                Check In / Out
              </GabButton>
            </>
          )}
        </GabCardBody>
      </GabCard>
    </div>
  );
}
